/**
 * Re-authentication Service
 * 
 * Provides secure re-authentication for sensitive operations like checkout.
 * Forces password/credential verification before proceeding with payment.
 * 
 * Industry standards:
 * - Payment processors require fresh authentication for high-value transactions
 * - Prevents session hijacking attacks from completing purchases
 * - Typical threshold: 15-30 minutes since last auth
 * 
 * @module services/reauthService
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, googleProvider } from './firebaseConfig';
import {
  reauthenticateWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  User,
  UserCredential,
} from 'firebase/auth';

/** Re-authentication configuration */
export interface ReauthConfig {
  /** Maximum age of last auth before requiring re-auth (ms). Default: 15 minutes */
  maxAuthAge?: number;
  /** Callback on successful re-auth */
  onSuccess?: () => void;
  /** Callback on re-auth failure */
  onFailure?: (error: Error) => void;
  /** Callback when user cancels re-auth */
  onCancel?: () => void;
}

/** Result of re-auth check */
export interface ReauthCheckResult {
  /** Whether re-auth is required */
  required: boolean;
  /** Reason for requiring re-auth */
  reason?: 'session_age' | 'no_user' | 'security_policy';
  /** Time since last authentication (ms) */
  authAge?: number;
}

/** Result of re-auth attempt */
export interface ReauthResult {
  success: boolean;
  error?: string;
  credential?: UserCredential;
}

// Default: 15 minutes
const DEFAULT_MAX_AUTH_AGE = 15 * 60 * 1000;

// Storage key for last auth timestamp
const LAST_AUTH_KEY = 'heseda-ai_last_auth';

/**
 * Record the timestamp of successful authentication
 * Called after login or successful re-auth
 */
export function recordAuthTimestamp(): void {
  localStorage.setItem(LAST_AUTH_KEY, Date.now().toString());
}

/**
 * Get the timestamp of last authentication
 */
export function getLastAuthTimestamp(): number | null {
  const stored = localStorage.getItem(LAST_AUTH_KEY);
  if (!stored) return null;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Clear auth timestamp (on logout)
 */
export function clearAuthTimestamp(): void {
  localStorage.removeItem(LAST_AUTH_KEY);
}

/**
 * Check if re-authentication is required before sensitive operation
 * 
 * @param user - Current Firebase user
 * @param maxAuthAge - Maximum allowed age of authentication (ms)
 * @returns Check result with required flag and reason
 * 
 * @example
 * ```typescript
 * const check = checkReauthRequired(auth.currentUser);
 * if (check.required) {
 *   // Show re-auth modal
 * }
 * ```
 */
export function checkReauthRequired(
  user: User | null,
  maxAuthAge: number = DEFAULT_MAX_AUTH_AGE
): ReauthCheckResult {
  // No user = definitely need auth
  if (!user) {
    return { required: true, reason: 'no_user' };
  }

  const lastAuth = getLastAuthTimestamp();
  
  // No recorded auth = need re-auth
  if (!lastAuth) {
    return { required: true, reason: 'session_age' };
  }

  const authAge = Date.now() - lastAuth;

  // Auth too old = need re-auth
  if (authAge > maxAuthAge) {
    return { 
      required: true, 
      reason: 'session_age',
      authAge 
    };
  }

  // Auth is fresh enough
  return { 
    required: false,
    authAge 
  };
}

/**
 * Perform re-authentication with the user's provider
 * Currently supports Google (primary), can be extended for other providers
 * 
 * @param user - Current Firebase user
 * @returns Result with success flag and optional credential
 * 
 * @example
 * ```typescript
 * const result = await performReauth(auth.currentUser);
 * if (result.success) {
 *   // Proceed with checkout
 * } else {
 *   showError(result.error);
 * }
 * ```
 */
export async function performReauth(user: User | null): Promise<ReauthResult> {
  if (!user) {
    return { success: false, error: 'No user logged in' };
  }

  try {
    // Determine provider (default to Google)
    const providerId = user.providerData[0]?.providerId || 'google.com';
    
    // Currently only Google re-auth is supported
    if (providerId === 'google.com' || providerId === 'google') {
      // Use native re-auth on Capacitor (iOS/Android)
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Using native Google re-auth via @capacitor-firebase/authentication');
        
        // Native sign-in (will prompt user to select account)
        const result = await FirebaseAuthentication.signInWithGoogle();
        
        if (!result.user) {
          throw new Error('No user returned from Google Sign-in');
        }
        
        // Verify it's the same user
        if (result.user.email !== user.email) {
          return { 
            success: false, 
            error: 'Please select the same account you are logged in with' 
          };
        }
        
        // Sync with Web SDK using the id token
        if (!result.credential?.idToken) {
          throw new Error('No idToken returned from Google Sign-in');
        }
        
        const webCredential = GoogleAuthProvider.credential(result.credential.idToken);
        const webResult = await signInWithCredential(auth, webCredential);
        
        console.log('✅ Native re-authentication successful:', webResult.user.email);
        
        // Record successful re-auth
        recordAuthTimestamp();
        
        return { success: true, credential: webResult };
      }
      
      // Web: Use popup re-auth
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      const credential = await reauthenticateWithPopup(user, googleProvider);
      
      // Record successful re-auth
      recordAuthTimestamp();
      
      console.log('✅ Re-authentication successful');
      return { success: true, credential };
    }

    // For other providers (Apple, Kakao), fall back to prompting re-login
    // This can be extended when needed
    return { 
      success: false, 
      error: 'Please sign out and sign in again to verify your identity' 
    };
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    console.error('❌ Re-authentication failed:', err.code, err.message);

    // Handle native errors
    const errorMsg = err.message || '';
    if (errorMsg.includes('cancel') || errorMsg.includes('Cancel') || 
        errorMsg.includes('12501') || errorMsg.includes('user_cancelled') ||
        errorMsg.includes('popup_closed') || errorMsg.includes('cancelled')) {
      return { success: false, error: 'Re-authentication cancelled' };
    }

    // User closed popup
    if (err.code === 'auth/popup-closed-by-user') {
      return { success: false, error: 'Re-authentication cancelled' };
    }

    // User cancelled
    if (err.code === 'auth/cancelled-popup-request') {
      return { success: false, error: 'Re-authentication cancelled' };
    }

    // Wrong account selected
    if (err.code === 'auth/user-mismatch') {
      return { success: false, error: 'Please select the same account you are logged in with' };
    }

    return { 
      success: false, 
      error: err.message || 'Re-authentication failed. Please try again.' 
    };
  }
}

/**
 * Check and perform re-auth if needed (combined helper)
 * 
 * @param user - Current Firebase user
 * @param maxAuthAge - Maximum auth age before re-auth required
 * @returns Result with success flag
 * 
 * @example
 * ```typescript
 * const result = await ensureFreshAuth(auth.currentUser);
 * if (!result.success) {
 *   showError(result.error);
 *   return;
 * }
 * // Proceed with payment
 * ```
 */
export async function ensureFreshAuth(
  user: User | null,
  maxAuthAge: number = DEFAULT_MAX_AUTH_AGE
): Promise<ReauthResult> {
  // On native platforms (iOS/Android), skip re-auth requirement
  // Native apps have device-level security (Face ID, fingerprint, PIN)
  // which provides equivalent security to web-based re-authentication
  if (Capacitor.isNativePlatform()) {
    console.log('📱 Native platform detected - skipping re-auth (device security applies)');
    return { success: true };
  }

  const check = checkReauthRequired(user, maxAuthAge);
  
  if (!check.required) {
    return { success: true };
  }

  console.log(`🔐 Re-authentication required (reason: ${check.reason})`);
  return performReauth(user);
}

export default {
  checkReauthRequired,
  performReauth,
  ensureFreshAuth,
  recordAuthTimestamp,
  getLastAuthTimestamp,
  clearAuthTimestamp,
};
